import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Link, useParams, useHistory } from 'react-router-dom';
import { Empty, Modal } from 'antd';
import { DesktopOutlined, CalendarOutlined } from '@ant-design/icons';
import Cards from 'design-components/Cards';
import { setTabMedia } from 'state/actions/breadcrumb';
import { regexInput } from 'utils/regex';
import ModalDelete from 'components/ModalDelete';
import { EmptyIcon } from 'assets/icon/bigicon';
import _ from 'lodash';
import {
  fetchTemplates,
  searchTemplates,
  deleteTemplate,
  filterTemplatesByScreenId,
} from 'state/actions/template';
import { fetchScreens } from 'state/actions/screens';
import { fetchMedia } from 'state/actions/media';
import DropdownWrapper from 'components/DropdownWrapper';
import CategoryDropdown from 'components/CategoryDropdown';
import TimelineSequence from 'components/TimelineSequence';
import moment from 'moment';
import './index.css';

function TemplateLibrary() {
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [screenOpen, setScreenOpen] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState('all');
  const [tempSelectedScreen, setTempSelectedScreen] = useState('all');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const dispatch = useDispatch();
  const { siteId, siteName } = useParams();
  const history = useHistory();

  const {
    userData,
    role,
    templateList,
    selectedTab,
    searchResults,
    filteredResults,
    screenList,
    mediaList,
  } = useSelector(
    (state) => ({
      userData: state.auth.userData,
      role: state.auth.userData?.role,
      templateList: state.template.list,
      selectedTab: state.breadcrumb.media,
      searchResults: state.template.searchResults,
      filteredResults: state.template.filteredResults,
      screenList: state.screens.list,
      mediaList: state.media?.list || [],
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(fetchTemplates({ siteId, type: selectedTab || 'published' }));
    dispatch(fetchScreens({ siteId }));
    dispatch(fetchMedia({ siteId, categoryId: 'all' }));
  }, [dispatch, siteId, selectedTab]);

  const handleSearch = (e) => {
    setError(null);
    const searchValue = e.target.value;
    if (!regexInput(searchValue) && searchValue) {
      setError('Search cannot use special characters');
    }
    setSearch(searchValue);
  };

  let data = templateList;
  if (search.trim().length > 0) {
    data = searchResults;
  } else if (selectedScreen && selectedScreen !== 'all') {
    data = filteredResults;
  }

  useEffect(() => {
    if (search.trim().length > 0) {
      dispatch(searchTemplates({ siteId, search, type: selectedTab }));
    }
  }, [dispatch, siteId, search, selectedTab]);

  useEffect(() => {
    if (selectedScreen && selectedScreen !== 'all') {
      dispatch(filterTemplatesByScreenId({ siteId, screenId: selectedScreen }));
    }
  }, [dispatch, siteId, selectedScreen]);

  const handleEdit = (id) => {
    history.push(`/${siteId}/information/${siteName}/template/${id}`);
  };

  const onTabClicked = (value) => {
    dispatch(setTabMedia(value));
  };

  const onOpenModalDelete = (templateId) => {
    setTemplateToDelete(templateId);
    setIsDeleteModalOpen(true);
    const template = templateList.find((t) => t._id === templateId);
    if (template && template.isArchived) {
      setDeleteMessage(
        'This action will permanently delete the template. Are you sure?'
      );
    } else {
      setDeleteMessage(
        'Are you sure you want to move this template to the archive?'
      );
    }
  };

  const onCloseModalDelete = () => {
    setIsDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const onDeleteTemplate = () => {
    if (templateToDelete) {
      dispatch(deleteTemplate({ siteId, id: templateToDelete }));
      setIsDeleteModalOpen(false);
      setTemplateToDelete(null);
    }
  };

  const getScreen = () => {
    if (selectedScreen === 'all') return 'All Screens';
    const screen = screenList.find((s) => s._id === selectedScreen);
    return screen
      ? screen.name || screen.screenName || `Screen ${selectedScreen}`
      : 'Select Screen';
  };

  const handleSetScreen = () => {
    setSelectedScreen(tempSelectedScreen);
    setScreenOpen(false);
  };

  const handlePreviewTemplate = (template) => {
    setPreviewTemplate(template);
    setIsPreviewModalOpen(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewTemplate(null);
  };

  const canCreate =
    (role && role === 'superadmin') ||
    userData?.roleAccess?.access?.find((acc) => acc.siteId === siteId)?.create;

  return (
    <div className="template-library">
      <div className="template-header">
        <div className="template-header-left">
          <div className="searchbar-wrapper">
            <div className="searchbar-container">
              <div className="searchbar">
                <i className="feather-search" />
                <input
                  placeholder="Search"
                  className="search-input"
                  value={search}
                  onChange={handleSearch}
                />
              </div>
              {error && <div className="search-error">{error}</div>}
            </div>
            <DropdownWrapper
              setIsOpen={setScreenOpen}
              isOpen={screenOpen}
              label={'Screen'}
              getItemLabel={getScreen}
              selectedItem={selectedScreen}
            >
              <CategoryDropdown
                categories={screenList}
                title={'Screen'}
                setTempSelectedCategory={(id) => setTempSelectedScreen(id)}
                modalOpen={screenOpen}
                handleSetPlaylist={handleSetScreen}
                tempSelectedCategory={tempSelectedScreen}
                filter={true}
              />
            </DropdownWrapper>
          </div>
          <div className="actions__wrapper">
            <div>
              {canCreate ? (
                <Link
                  to={`/${siteId}/information/${siteName}/template/new`}
                  className="btn-primary"
                >
                  <i className="feather-plus mr-1" />
                  Add Template
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="template-content-wrapper">
        <div className="template-content">
          <div className="template-tab-wrapper">
            <div className="template-tab-left">
              <div
                onClick={() => onTabClicked('published')}
                className={`template-tab ${
                  selectedTab === 'published' || !selectedTab ? 'active' : ''
                }`}
              >
                Published Items
              </div>
              <div
                onClick={() => onTabClicked('unpublished')}
                className={`template-tab ${
                  selectedTab === 'unpublished' ? 'active' : ''
                }`}
              >
                Unpublished Items
              </div>
              <div
                onClick={() => onTabClicked('archived')}
                className={`template-tab ${
                  selectedTab === 'archived' ? 'active' : ''
                }`}
              >
                Archived Items
              </div>
            </div>
          </div>
          <div className="template-list-content">
            {data && data.length > 0 ? (
              _.chunk(data, 3).map((chunk, index) => (
                <div className="template-list-chunk" key={index}>
                  {chunk.map((template) => (
                    <div
                      key={template._id}
                      data-id={template._id}
                      className="template-list"
                    >
                      <Cards
                        data={template}
                        type="template"
                        isShowMedia={true}
                        editAction={() => handleEdit(template._id)}
                        deleteAction={() => onOpenModalDelete(template._id)}
                        previewTemplateAction={() =>
                          handlePreviewTemplate(template)
                        }
                        isPreview={false}
                        isDownload={false}
                      />
                    </div>
                  ))}
                  {Array(3 - chunk.length)
                    .fill(0)
                    .map((_v, i) => (
                      <div
                        key={`empty-${i}`}
                        className="template-list-empty"
                      />
                    ))}
                </div>
              ))
            ) : (
              <div className="template-empty">
                <Empty image={EmptyIcon} description="No Templates Found" />
              </div>
            )}
          </div>
          {isDeleteModalOpen && (
            <ModalDelete
              isVisible={isDeleteModalOpen}
              title={'Delete Template'}
              onOk={onDeleteTemplate}
              onCancel={onCloseModalDelete}
              deleteMessage={deleteMessage}
            />
          )}

          {isPreviewModalOpen && previewTemplate && (
            <Modal
              title="Show Template"
              open={isPreviewModalOpen}
              onCancel={closePreviewModal}
              footer={null}
              width={1200}
              style={{ top: 20 }}
              className="template-preview-modal"
            >
              <div style={{ marginBottom: '24px' }}>
                <h2
                  style={{
                    color: '#4A90E2',
                    fontSize: '24px',
                    fontWeight: '600',
                    margin: '0 0 16px 0',
                  }}
                >
                  {previewTemplate.name}
                </h2>
                {previewTemplate.description && (
                  <p
                    style={{
                      color: '#666',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      margin: '0 0 20px 0',
                    }}
                  >
                    {previewTemplate.description}
                  </p>
                )}

                <div
                  style={{
                    display: 'flex',
                    gap: '24px',
                    marginBottom: '20px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <DesktopOutlined style={{ color: '#64D5D2' }} />
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      {(() => {
                        if (
                          previewTemplate.screenIds &&
                          previewTemplate.screenIds.length > 0
                        ) {
                          return previewTemplate.screenIds
                            .map((screenId) => {
                              const screen = screenList.find(
                                (s) => s._id === screenId
                              );
                              return screen
                                ? screen.name ||
                                    screen.screenName ||
                                    `Screen ${screenId}`
                                : `Screen ${screenId}`;
                            })
                            .join(' • ');
                        }
                        return 'No screens assigned';
                      })()}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <CalendarOutlined style={{ color: '#64D5D2' }} />
                    <span style={{ color: '#666', fontSize: '14px' }}>
                      {previewTemplate.start && previewTemplate.end
                        ? `${moment
                            .unix(previewTemplate.start)
                            .format('DD MMM YYYY')} - ${moment
                            .unix(previewTemplate.end)
                            .format('DD MMM YYYY')}`
                        : 'No schedule set'}
                    </span>
                  </div>
                </div>

                <div style={{ marginTop: '24px' }}>
                  <h3
                    style={{
                      color: '#4A90E2',
                      fontSize: '18px',
                      fontWeight: '600',
                      margin: '0 0 16px 0',
                    }}
                  >
                    Timeline :
                  </h3>
                  <div
                    style={{
                      border: '1px solid #e1e5e9',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <TimelineSequence
                      duration={previewTemplate.totalDuration || 60}
                      mediaList={mediaList}
                      allMediaList={mediaList}
                      programmaticVendors={[]}
                      initialSlots={previewTemplate.slots || []}
                      onSlotsChange={() => {}}
                      isReadOnly={true}
                    />
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplateLibrary;
